import { formatPrice } from '@/utils/circuits/prices/formatPrice';
import { formatCurrency } from '@/utils/prices/formatCurrency';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import i18next from '@/i18n/config';

export type ReservationSummary = {
  id: string;
  ref: number;
  circuit: {
    company: {
      id: string;
      name: string;
    };
    currencies: string[];
  } | null;
  project: {
    id: string;
    client: {
      id: string;
      agencyName?: string | null;
      mainContact: {
        firstName: string;
        lastName: string;
      };
    };
  };
  _count: {
    adults: number;
    children: number;
  };
  amount: number | null;
  amountWithoutMarginRate?: number | null;
  reservationAgent: {
    agency: {
      name: string;
    };
    firstname: string;
    lastname: string;
  };
  arrivalDate: string | null;
  confirmedAt: string | null;
  createdAt: string;
  reservationRequestSentAt: string | null;
};

export const getReservationsSummaryColumns = (
  isTourOperator = false,
): ColumnDef<ReservationSummary>[] => {
  const t = i18next.t.bind(i18next);
  const clientColumnHeader = isTourOperator
    ? t('distributorAgencies.columns.agency')
    : t('reservations.columns.client');

  return [
    {
      accessorKey: 'ref',
      header: t('reservations.columns.code'),
      cell: ({ row }) => `D${row.original.ref.toString().padStart(3, '0')}`,
    },
    {
      accessorKey: 'project.client.mainContact.firstName',
      header: clientColumnHeader,
      cell: ({ row }) => {
        if (isTourOperator) {
          return (
            row.original.project.client.agencyName ??
            row.original.reservationAgent.agency.name ??
            '-'
          );
        }
        return `${row.original.project.client.mainContact.firstName} ${row.original.project.client.mainContact.lastName}`;
      },
    },
    {
      accessorKey: '_count.adults',
      header: t('reservations.columns.pax'),
      cell: ({ row }) =>
        `${row.original._count.adults + row.original._count.children}`,
    },
    {
      accessorKey: 'amount',
      header: t('reservations.columns.amount'),
      cell: ({ row }) => {
        if (!row.original.amount) return '-';
        const currencyCode = row.original.circuit?.currencies[0];
        const currency =
          currencyCode && currencyCode !== 'EUR'
            ? formatCurrency(currencyCode)
            : '€';
        return `${formatPrice(row.original.amount)} ${currency}`;
      },
    },
    {
      accessorKey: 'amountWithoutMarginRate',
      header: t('reservations.columns.amountWithoutMarginRate'),
      cell: ({ row }) => {
        const currencyCode = row.original.circuit?.currencies[0];
        const currency =
          currencyCode && currencyCode !== 'EUR'
            ? formatCurrency(currencyCode)
            : '€';
        return row.original.amountWithoutMarginRate
          ? `${formatPrice(row.original.amountWithoutMarginRate)} ${currency}`
          : '-';
      },
    },
    {
      accessorKey: 'circuit.company.name',
      header: t('reservations.columns.supplier'),
      cell: ({ row }) => row.original.circuit?.company.name ?? '-',
    },
    {
      accessorKey: 'reservationAgent.agency.name',
      header: t('reservations.columns.distributor'),
    },
    {
      accessorKey: 'reservationAgent.firstname',
      header: t('reservations.columns.agent'),
      cell: ({ row }) =>
        `${row.original.reservationAgent.firstname} ${row.original.reservationAgent.lastname}`,
    },
    {
      accessorKey: 'arrivalDate',
      header: t('reservations.columns.departureDate'),
      cell: ({ row }) =>
        row.original.arrivalDate
          ? format(new Date(row.original.arrivalDate), 'dd/MM/yyyy')
          : '-',
    },
    {
      accessorKey: 'createdAt',
      header: t('reservations.columns.creationDate'),
      cell: ({ row }) => format(new Date(row.original.createdAt), 'dd/MM/yyyy'),
    },
    {
      accessorKey: 'confirmedAt',
      header: t('reservations.columns.confirmationDate'),
      cell: ({ row }) =>
        row.original.confirmedAt
          ? format(new Date(row.original.confirmedAt), 'dd/MM/yyyy')
          : '-',
    },
    {
      accessorKey: 'reservationRequestSentAt',
      header: t('reservations.columns.reservationRequestDate'),
      cell: ({ row }) =>
        row.original.reservationRequestSentAt
          ? format(
              new Date(row.original.reservationRequestSentAt),
              'dd/MM/yyyy',
            )
          : '-',
    },
  ];
};

/** @deprecated Use getReservationsSummaryColumns(false) */
export const RESERVATIONS_SUMMARY_COLUMNS =
  getReservationsSummaryColumns(false);
